import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { Goal, GoalDetail, GoalSummary } from "@/lib/types"

interface GoalFilters {
  user_id?: string
  status?: string
  period?: string
  metric_type?: string
  q?: string
}

export function useGoals(filters?: GoalFilters) {
  const params = new URLSearchParams()
  if (filters?.user_id) params.set("user_id", filters.user_id)
  if (filters?.status) params.set("status", filters.status)
  if (filters?.period) params.set("period", filters.period)
  if (filters?.metric_type) params.set("metric_type", filters.metric_type)
  if (filters?.q) params.set("q", filters.q)
  const qs = params.toString()

  return useQuery({
    queryKey: ["goals", filters],
    queryFn: () => api.get<{ data: Goal[] }>(`/api/v1/goals${qs ? `?${qs}` : ""}`),
  })
}

export function useGoal(id: string) {
  return useQuery({
    queryKey: ["goals", id],
    queryFn: () => api.get<{ data: GoalDetail }>(`/api/v1/goals/${id}`),
    enabled: !!id,
  })
}

export function useGoalSummary(userId?: string) {
  const params = userId ? `?user_id=${userId}` : ""
  return useQuery({
    queryKey: ["goals", "summary", userId],
    queryFn: () => api.get<{ data: GoalSummary }>(`/api/v1/goals/summary${params}`),
  })
}

export function useCreateGoal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: Goal }>("/api/v1/goals", { goal: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goals"] })
    },
  })
}

export function useUpdateGoal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: Goal }>(`/api/v1/goals/${id}`, { goal: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goals"] })
    },
  })
}

export function useDeleteGoal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/goals/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goals"] })
    },
  })
}

export function useRecalculateGoal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: GoalDetail }>(`/api/v1/goals/${id}/recalculate`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["goals"] })
      qc.invalidateQueries({ queryKey: ["goals", id] })
    },
  })
}
